// newApplicantsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { CPD_STATUS } from "../../data/cpdConstants";
import { atpService } from "../../services/atpService";
import { toast } from "react-toastify";
import { userInfoService } from "../../services/userInfoService";

// initial state
const initialState = {
    loading: false,
    error: null,
    data: {
        current_page: 1,
        page_limit: 10,
        total_pages: 1,
    },
    search: "",
    CPDStatus: CPD_STATUS.APPROVED,
    startDate: null,
    endDate: null,
    sortParameter: "created_at",
    orderParameter: "DESC",
};

export const fetchDashboardMetrics = createAsyncThunk("atp/fetchDashboardMetrics", async (data) => {
    try {
        const response = await userInfoService.dashboard(data);
        return response?.data?.data;
    } catch (error) {
        toast.error(error?.response?.data?.message);
        throw error;
    }
});

export const fetchCPDData = createAsyncThunk(
    "atp/fetchCPDData",
    async (
        {
            page,
            limit,
            cpd_status,
            start_date,
            end_date,
            title,
            location,
            duration_time,
            duration_type,
            date_time,
            tech_field,
            target_audience,
            type_of_cpd,
            web_url,
            description,
            approval_id,
            approval_date,
            sort,
            order,
        },
        thunkAPI
    ) => {
        try {
            const response = await atpService.getCPDS({
                page,
                limit,
                cpd_status,
                start_date,
                end_date,
                title,
                location,
                duration_time,
                duration_type,
                date_time,
                tech_field,
                target_audience,
                type_of_cpd,
                web_url,
                description,
                approval_id,
                approval_date,
                sort,
                order,
            });
            return response?.data?.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response.data); // Reject with error message
        }
    }
);

export const fetchCPDDataCounts = createAsyncThunk(
    "atp/fetchCPDDataCounts",
    async (
        {
            cpd_status,
            start_date,
            end_date,
            title,
            location,
            duration_time,
            duration_type,
            date_time,
            tech_field,
            target_audience,
            type_of_cpd,
            web_url,
            description,
            approval_id,
            approval_date,
            sort,
            order,
        },
        thunkAPI
    ) => {
        try {
            const response = await atpService.getCPDSCounts({
                cpd_status,
                start_date,
                end_date,
                title,
                location,
                duration_time,
                duration_type,
                date_time,
                tech_field,
                target_audience,
                type_of_cpd,
                web_url,
                description,
                approval_id,
                approval_date,
                sort,
                order,
            });
            return response;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response.data); // Reject with error message
        }
    }
);

export const deleteCPD = createAsyncThunk("atp/deleteCPD", async (id, { dispatch }) => {
    try {
        const response = await atpService.deleteCPD(id);
        toast.success(response?.data?.message);
    } catch (error) {
        toast.error(error?.response?.data?.message);
        throw error;
    }
});

export const getCPDById = createAsyncThunk("atp/getCPDById", async (id, { dispatch }) => {
    try {
        const response = await atpService.getCPDById(id);
        return response?.data?.data;
    } catch (error) {
        toast.error(error?.response?.data?.message);
        throw error;
    }
});

export const updateCPD = createAsyncThunk("atp/deleteCPD", async ({ id, data }, { dispatch }) => {
    try {
        const response = await atpService.updateCPD(id, data);
        toast.success(response?.data?.message);
    } catch (error) {
        toast.error(error?.response?.data?.message);
        throw error;
    }
});

export const fetchResources = createAsyncThunk("atp/fetchResources", async () => {
    try {
        const response = await atpService.getResources();
        return response?.data?.data;
    } catch (error) {
        toast.error(error?.response?.data?.message);
        throw error;
    }
});

export const fetchATPSubscription = createAsyncThunk("workshop/fetchATPSubscription", async () => {
    try {
        const response = await atpService.getAtpSubscription();
        return response?.data?.data;
    } catch (error) {
        toast.error(error?.response?.data?.message);
        throw error;
    }
});

// slice
const tablesSlice = createSlice({
    name: "newApplicants",
    initialState,
    reducers: {
        handleLoading: (state, action) => {
            state.loading = action.payload;
        },
        handleCPDStatus: (state, action) => {
            state.CPDStatus = action.payload;
        },
        handleCurrentPage: (state, action) => {
            state.data.current_page = action.payload;
        },
        setSearch: (state, action) => {
            state.search = action.payload;
        },
        resetData: (state, action) => {
            state.data = {
                current_page: 1,
                page_limit: 10,
                total_pages: 1,
            };
            state.search = "";
        },
        setStartDate: (state, action) => {
            state.startDate = action.payload;
        },
        setEndDate: (state, action) => {
            state.endDate = action.payload;
        },
        setSortOrderParameter: (state, action) => {
            state.sortParameter = action.payload.sort;
            state.orderParameter = action.payload.order;
        },
        handleLogoutAtp: (state, action) => {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCPDData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCPDData.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
                if (action.payload.total_pages === 0) {
                    state.data.total_pages = 1;
                } else {
                    state.data.total_pages = action.payload.total_pages;
                }
            })
            .addCase(fetchCPDData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(fetchResources.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchResources.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(fetchResources.rejected, (state, action) => {
                state.loading = false;
            })
            .addCase(fetchDashboardMetrics.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchDashboardMetrics.fulfilled, (state, action) => {
                state.loading = false;
                state.dashboard = action.payload;
            })
            .addCase(fetchDashboardMetrics.rejected, (state, action) => {
                state.loading = false;
            })
            .addCase(fetchATPSubscription.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchATPSubscription.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(fetchATPSubscription.rejected, (state, action) => {
                state.loading = false;
            });
    },
});

// Export the actions and reducer
export const { handleLoading, handleCPDStatus, handleCertificateType, handleCurrentPage, setSearch, resetData, setStartDate, setEndDate, setSortOrderParameter, handleLogoutAtp } = tablesSlice.actions;
export default tablesSlice.reducer;
